import type {
  CodeExecutionHookParams,
  CodeExecutionHookReturn,
} from "../useProvider";

import * as React from "react";

import { skipToken, useQuery } from "@tanstack/react-query";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import {
  useSandboxStateContext,
  useSandboxStateStore,
} from "@v2/store/SandboxStateStoreContext";
import {
  daytonaClient,
  daytonaOptions,
  daytonaRouter,
} from "@v2/trpc/routes/daytona";
import isEqual from "lodash-es/isEqual";
import z from "zod";

export function useDaytona({
  processLogMessage,
  projectId,
  incrementRefreshEpoch,
  forceSkip,
}: CodeExecutionHookParams): CodeExecutionHookReturn {
  const { store } = useSandboxStateContext();
  const viewingVersionId = useSandboxStateStore(
    (state) => state.viewingVersionId,
  );
  const currentStyleLibrary = useSandboxStateStore(
    (state) => state.currentStyleLibrary,
  );

  const [includeScaffold] = useLocalStorageState(
    "replo.debug.includeScaffoldFilesInSandboxSync",
    false,
    { schema: z.boolean() },
  );

  const { mutate: syncSandboxState, isPending: isSyncPending } =
    daytonaRouter.syncSandboxState.useMutation({});

  const { mutate: initializeSandboxTemplates } =
    daytonaRouter.initializeSandboxTemplates.useMutation({});

  const { data: userProjectSandboxData, isLoading } = useQuery(
    daytonaOptions.getUserProjectSandbox.queryOptions(
      projectId && !forceSkip
        ? {
            projectId,
          }
        : skipToken,
      {
        // NOTE (Bobby, 2025-09-19): Setting the stale time to 30 seconds so that if window focus is triggered multiple times in a short period (switching tabs back and forth)
        // we don't make multiple requests to the server. I think this time needs to be at least as long as the refetchInterval. This is basically telling react query the minimum
        // amount of time that needs to pass before we consider the data stale and make any new requests. This gives enough buffer to allow the first getPreviewUrl request to finish
        // so we never run into issues with race conditions around calling that multiple times.
        staleTime: 30000,
        refetchOnWindowFocus: true,
        // NOTE (Bobby, 2025-09-19): While the user's actively on the tab, we want to keep refetching and keeping the sandbox alive.
        refetchInterval: 30000,
        refetchIntervalInBackground: false,
        enabled: !isSyncPending,
      },
    ),
  );

  // NOTE (Nick, 2026-02-19): DEBUG — Track getUserProjectSandbox responses to see if URL changes or wasRestarted flips
  const previousDebugUrlRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const currentUrl = userProjectSandboxData?.url;
    const wasRestarted = userProjectSandboxData?.wasRestarted;
    const urlChanged =
      previousDebugUrlRef.current !== undefined &&
      previousDebugUrlRef.current !== currentUrl;
    processLogMessage(
      `[debug] getUserProjectSandbox: url=${currentUrl ?? "undefined"}, wasRestarted=${wasRestarted}, urlChanged=${urlChanged}`,
    );
    previousDebugUrlRef.current = currentUrl;
  }, [userProjectSandboxData, processLogMessage]);

  const { reset: resetLogSubscription } =
    daytonaRouter.streamLogs.useSubscription(
      projectId && !forceSkip && userProjectSandboxData?.url
        ? { projectId }
        : skipToken,
      {
        onData: (data: { isHistorical: boolean; message: string }) => {
          if (data.isHistorical) {
            processLogMessage(`[HISTORICAL] ${data.message}`);
          } else {
            processLogMessage(data.message);
          }
        },
        onError: (error) => {
          setError(error);
        },
      },
    );

  const [error, setError] = React.useState<unknown | undefined>(undefined);

  // NOTE (Bobby, 2025-10-18): This useEffect is used to update the sandbox state whenever there's a change to any of the sandbox state values.
  React.useEffect(() => {
    if (projectId && userProjectSandboxData?.url) {
      void syncSandboxState(
        {
          projectId,
          viewingVersionId: viewingVersionId ?? undefined,
          currentStyleLibrary: currentStyleLibrary ?? undefined,
          includeScaffold,
        },
        {
          onSuccess: (_data, variables) => {
            const currentState = store.getState();
            const currentParams = {
              viewingVersionId: currentState.viewingVersionId ?? undefined,
              currentStyleLibrary:
                currentState.currentStyleLibrary ?? undefined,
            };
            const syncedParams = {
              viewingVersionId: variables.viewingVersionId,
              currentStyleLibrary: variables.currentStyleLibrary,
            };
            if (!isEqual(syncedParams, currentParams)) {
              return;
            }

            if (includeScaffold) {
              incrementRefreshEpoch();
            }
          },
        },
      );
    }
  }, [
    projectId,
    viewingVersionId,
    currentStyleLibrary,
    userProjectSandboxData?.url,
    syncSandboxState,
    includeScaffold,
    incrementRefreshEpoch,
    store,
  ]);

  // NOTE (Bobby, 2025-09-19): This useEffect is used to make sure we reset the log subscription as well as
  // increment the refresh epoch so that we tell the iframe to make a request to that page.
  React.useEffect(() => {
    // NOTE (Nick, 2026-02-20): Skip when the query hasn't resolved yet —
    // there's no data to act on and the else branch would misleadingly log
    // "Dev server already running" when the sandbox state is unknown.
    if (!userProjectSandboxData) {
      return;
    }

    if (userProjectSandboxData.wasRestarted) {
      processLogMessage("🔄 Dev server restarted - refreshing iframe");
      if (projectId) {
        void syncSandboxState(
          {
            projectId,
            includeScaffold,
          },
          {
            onSuccess: () => {
              resetLogSubscription();
              incrementRefreshEpoch();
            },
          },
        );
        void initializeSandboxTemplates({
          projectId,
        });
      }
    } else {
      processLogMessage("Client: Dev server already running");
    }
  }, [
    userProjectSandboxData,
    projectId,
    incrementRefreshEpoch,
    resetLogSubscription,
    processLogMessage,
    syncSandboxState,
    includeScaffold,
    initializeSandboxTemplates,
  ]);

  const restartDevServer = React.useCallback(async () => {
    if (!projectId) {
      return;
    }
    await daytonaClient.restartDevServer.mutate({
      projectId,
    });
    resetLogSubscription();
  }, [resetLogSubscription, projectId]);

  return {
    error,
    url: userProjectSandboxData?.url,
    isLoading,
    restartDevServer,
    resetLogSubscription,
    isSyncPending,
  };
}
